import { useMemo, useState } from "react";

import { Button } from "@blueprintjs/core";
import { useNavigate } from "react-router";

import { Box } from "@/components/layout/flexbox";
import { OverlapMeetingBanner } from "@/components/pieces/coaching/meetings/OverlapMeetingBanner";
import { UnfulfilledOrders } from "@/components/pieces/coaching/UnfulfilledOrders";
import { StarRating } from "@/components/pieces/StarRating";
import { CARD_CLASSES } from "@/css/constants";
import { useSeeMoreMeetingsLazyQuery } from "@/graphql";
import { ParticipantMeeting } from "@/types/meetings";
import { RoleView, SessionsUser, StaffSessionsUser } from "@/types/users";
import { ensureNonNullArray } from "@/utils/array";

import { MeetingBasics } from "./MeetingBasics";
import {
  buildOverlapMeetingList,
  getMeetingStatus,
  getUserBufferSetting,
  SessionsMeeting,
  StaffMeeting,
} from "./utils";

// keep in sync with the constant on the back with the same name
const NUM_MEETINGS_PER_PAGE = 15;

export type UserMeeting = (SessionsMeeting | StaffMeeting) & {
  backgroundColor?: string;
};

interface MeetingProps {
  meeting: UserMeeting;
  customerId?: string;
  view?: RoleView;
  mt?: number;
}

export function Meeting({
  meeting,
  customerId,
  view = "coach",
  mt,
}: MeetingProps) {
  return (
    <Box
      className="round-corners bas hover:bg-blue-50"
      px={24}
      py={3}
      mt={mt}
      css={{ backgroundColor: meeting.backgroundColor }}
    >
      <MeetingBasics
        meeting={meeting}
        showMessageButton={true}
        chatChannelId={meeting.chatChannelId}
        showMessageCount={true}
        view={view}
        customerId={customerId}
      />
      {meeting.canLeaveRating ? <RateMeeting meeting={meeting} /> : null}
    </Box>
  );
}

interface RateMeetingProps {
  meeting: UserMeeting | ParticipantMeeting;
}

export function RateMeeting({ meeting }: RateMeetingProps) {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col gap-3">
      <div className="xs:text-sm font-medium text-gray-800 md:text-base">
        How helpful was {meeting.coach?.name} during this session?
      </div>
      <div>
        <StarRating
          onSelect={(s) => navigate(`/rate?mid=${meeting.id}&score=${s}`)}
        />
      </div>
    </div>
  );
}

interface ViewMoreMeetingsProps {
  customerId: string;
  startingIndex: number;
}

function ViewMoreMeetings({
  customerId,
  startingIndex,
}: ViewMoreMeetingsProps) {
  const [index, setIndex] = useState(startingIndex);
  const [meetings, setMeetings] = useState<UserMeeting[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const [fetch] = useSeeMoreMeetingsLazyQuery({
    onCompleted: (data) => {
      setLoading(false);
      const newMeetings = ensureNonNullArray(data.meetings);
      if (newMeetings.length < 0) {
        setHasMore(false);
        return;
      }

      if (newMeetings.length < NUM_MEETINGS_PER_PAGE) {
        setHasMore(false);
      }

      setIndex(index + newMeetings.length);
      setMeetings((oldMeetings) => [...oldMeetings, ...newMeetings]);
    },
  });

  function load() {
    setLoading(true);
    fetch({
      variables: {
        customerId,
        index,
      },
    });
  }

  return (
    <Box mt={meetings.length ? 3 : undefined}>
      {meetings.map((meeting, index) => {
        return (
          <Meeting
            key={meeting.id}
            customerId={customerId}
            meeting={meeting}
            mt={index > 0 ? 3 : undefined}
          />
        );
      })}
      {hasMore ? (
        <Box mt={3}>
          <Button loading={loading} onClick={load}>
            Load More
          </Button>
        </Box>
      ) : null}
    </Box>
  );
}

interface MeetingListProps {
  user: (SessionsUser | StaffSessionsUser) & {
    unfulfilledOrders?: SessionsUser["unfulfilledOrders"];
  };
  customerId?: string;
}

export function MeetingList({ user, customerId }: MeetingListProps) {
  const meetings = ensureNonNullArray(user.meetings);
  const bookingRequestMeetings = ensureNonNullArray(
    user.bookingRequestMeetings,
  );

  const meetingsByStatus = useMemo(() => {
    if (meetings.length === 0) {
      return { ongoing: [], upcoming: [], past: [] };
    }

    return ensureNonNullArray(meetings).reduce(
      (agg: Record<string, UserMeeting[]>, m: UserMeeting) => {
        const status = getMeetingStatus(m);
        const key = ["CANCELED", "COMPLETE"].includes(status)
          ? "past"
          : status === "SOON"
            ? "ongoing"
            : "upcoming";
        agg[key].push(m);
        return agg;
      },
      { ongoing: [], upcoming: [], past: [] },
    );
  }, [meetings]);

  const hasOngoing = !!meetingsByStatus.ongoing.length;
  const hasUpcoming = !!meetingsByStatus.upcoming.length;
  const hasBookingRequest = !!bookingRequestMeetings?.length;
  const hasPast = !!meetingsByStatus.past.length;

  const upcomingMeetingList = useMemo(() => {
    const { upcoming = [] } = meetingsByStatus;
    const { beforeBuffer, afterBuffer } = getUserBufferSetting(user);
    return buildOverlapMeetingList({
      meetingLists: upcoming,
      beforeBuffer,
      afterBuffer,
    });
  }, [meetingsByStatus, user]);

  if (meetings.length === 0) {
    return (
      <Box textAlign="center" mt={3}>
        <i>No sessions to display</i>
      </Box>
    );
  }

  const renderMeeting = (meeting: UserMeeting) => {
    return (
      <Meeting
        key={meeting.id}
        customerId={customerId}
        meeting={meeting}
        mt={3}
      />
    );
  };

  const sorted = (meetings: UserMeeting[], desc: boolean) => {
    return [...meetings].sort(function (a, b) {
      if (!a.eventAt || !b.eventAt) return 0;
      const keyA = new Date(a.eventAt);
      const keyB = new Date(b.eventAt);
      if (keyA < keyB) return desc ? 1 : -1;
      if (keyA > keyB) return desc ? -1 : 1;
      return 0;
    });
  };

  const unfulfilledOrders = ensureNonNullArray(user.unfulfilledOrders);
  const hasUnfulfilledOrders = unfulfilledOrders.length > 0;

  return (
    <>
      {hasUnfulfilledOrders ? <UnfulfilledOrders user={user} /> : null}
      <OverlapMeetingBanner
        meetings={meetingsByStatus && meetingsByStatus.upcoming}
        user={user}
      />
      {hasBookingRequest ? (
        <Box
          className={CARD_CLASSES}
          backgroundColor="#FFF"
          py={[3, 3, 3, 24]}
          px={[3, 3, 3, 4]}
          mt={hasUnfulfilledOrders ? 4 : 0}
          mb={4}
        >
          <Box fontWeight={600} fontSize={20}>
            Pending Booking Requests
          </Box>
          {sorted(bookingRequestMeetings, false).map(renderMeeting)}
        </Box>
      ) : null}
      {hasOngoing ? (
        <Box
          className={CARD_CLASSES}
          backgroundColor="#FFF"
          py={[3, 3, 3, 24]}
          px={[3, 3, 3, 4]}
          mt={hasUnfulfilledOrders ? 4 : 0}
        >
          <Box fontWeight={600} fontSize={20}>
            Ongoing Sessions
          </Box>
          {sorted(meetingsByStatus.ongoing, false).map(renderMeeting)}
        </Box>
      ) : null}
      {hasUpcoming ? (
        <Box
          className={CARD_CLASSES}
          backgroundColor="#FFF"
          py={[3, 3, 3, 24]}
          px={[3, 3, 3, 4]}
          mt={hasOngoing || hasUnfulfilledOrders ? 4 : 0}
        >
          <Box fontWeight={600} fontSize={20}>
            Upcoming Sessions
          </Box>
          {sorted(upcomingMeetingList, false).map(renderMeeting)}
        </Box>
      ) : null}
      {hasPast ? (
        <Box
          className={CARD_CLASSES}
          backgroundColor="#FFF"
          py={[3, 3, 3, 24]}
          px={[3, 3, 3, 4]}
          mt={hasOngoing || hasUpcoming || hasUnfulfilledOrders ? 4 : 0}
        >
          <Box fontWeight={600} fontSize={20}>
            Past Sessions
          </Box>
          {sorted(meetingsByStatus.past, true).map(renderMeeting)}
          {meetings.length >= NUM_MEETINGS_PER_PAGE && customerId && (
            <ViewMoreMeetings
              customerId={customerId}
              startingIndex={meetings.length}
            />
          )}
        </Box>
      ) : null}
    </>
  );
}
